import { useRef, useState } from 'react'
import { findPlate, gridCells, PLATE_DEFAULTS } from './plate.js'
import { placeMarkOnQuad, loadImage, canvasToBlob } from './placeMark.js'
import { uploadOwned, blobToDataUrl } from '../ownedAsset.js'
import { MARK_SOURCE } from '../../../lib/championToClose.js'
import MarkRung from '../MarkRung.jsx'
import styles from './PlaceRealMark.module.css'

/**
 * RUNG 2, AS A THING A REP DOES.
 *
 * ⚠️ WHY THIS EXISTS AS A PANEL AND NOT A SCRIPT. The composite was proven on
 * 2026-09-18 and again on 2026-09-19, and neither proof was any use to anybody:
 * one ran in Python on my machine and the other was a screenshot. A capability
 * a rep cannot reach is not a capability.
 *
 * WHAT IT DOES. Takes the reserved-plate sheet that just came back from the
 * GPT, finds the empty plate on every housing, and places the school's REAL
 * owned mark file onto each one. The output is uploaded through the same
 * owned-asset route as everything else and carries
 * `owned-crest-on-generated-housing`, which is rung 2: the mark is genuine,
 * the ring is not.
 *
 * ⚠️ IT NEVER CLAIMS RUNG 3. The housing underneath came out of a model. The
 * badge says so, the provenance says so, and nothing here can write
 * `owned-crest-composited`.
 */
export default function PlaceRealMark({ sheetUrl, markUrl, teamName, onPlaced }) {
  const [rows, setRows] = useState(3)
  const [cols, setCols] = useState(7)
  const [state, setState] = useState('idle')
  const [msg, setMsg] = useState({ tone: '', text: '' })
  const [result, setResult] = useState(null)
  const [found, setFound] = useState(null)
  const previewRef = useRef(null)

  const say = (tone, text) => setMsg({ tone, text })

  async function run() {
    if (!sheetUrl || !markUrl) return
    setState('working')
    say('', '')
    setResult(null)
    const t0 = Date.now()
    try {
      const [sheet, mark] = await Promise.all([loadImage(sheetUrl), loadImage(markUrl)])

      const base = document.createElement('canvas')
      base.width = sheet.naturalWidth
      base.height = sheet.naturalHeight
      base.getContext('2d').drawImage(sheet, 0, 0)

      const cells = gridCells(base.width, base.height, { rows: Number(rows), cols: Number(cols) })
      const ctx = base.getContext('2d')
      const hits = []
      for (const cell of cells) {
        if (cell.w < 8 || cell.h < 8) continue
        const data = ctx.getImageData(cell.x, cell.y, cell.w, cell.h)
        const plate = findPlate(data, PLATE_DEFAULTS)
        if (!plate) continue
        /* Cell coordinates back into sheet coordinates. */
        const q = plate.quad
        hits.push({
          cell,
          quad: {
            tl: [q.tl[0] + cell.x, q.tl[1] + cell.y],
            tr: [q.tr[0] + cell.x, q.tr[1] + cell.y],
            br: [q.br[0] + cell.x, q.br[1] + cell.y],
            bl: [q.bl[0] + cell.x, q.bl[1] + cell.y],
          },
          area: plate.area,
        })
      }
      setFound({ hits: hits.length, of: cells.length })

      if (!hits.length) {
        setState('idle')
        say('warn', `No empty plate was found on any of the ${cells.length} housings. `
          + 'That usually means the sheet was generated with the mark drawn on rather than with the plates reserved. '
          + 'Turn on "Leave every plate EMPTY" above and generate the sheet again.')
        return
      }

      let canvas = base
      for (const h of hits) canvas = placeMarkOnQuad(canvas, mark, h.quad)

      if (previewRef.current) {
        previewRef.current.width = canvas.width
        previewRef.current.height = canvas.height
        previewRef.current.getContext('2d').drawImage(canvas, 0, 0)
      }

      const blob = await canvasToBlob(canvas)
      const at = new Date().toISOString()
      const owned = await uploadOwned({
        name: `${(teamName || 'job').replace(/[^\w-]+/g, '_')}-bezel-drop-real-mark-${at.replace(/[-:]/g, '').replace(/\.\d+Z$/, 'Z')}.png`,
        kind: 'gpt-bridge',
        dataUrl: await blobToDataUrl(blob),
      })

      const out = {
        url: owned.url,
        fileId: owned.fileId || '',
        at,
        markSource: MARK_SOURCE.OWNED_ON_GENERATED,
        placed: hits.length,
        of: cells.length,
        width: canvas.width,
        height: canvas.height,
        bytes: blob.size,
        seconds: Math.round((Date.now() - t0) / 1000),
      }
      setResult(out)
      setState('idle')
      onPlaced?.(out)
      say('ok', `The school's real mark is placed on ${hits.length} of ${cells.length} housings and the sheet is owned in HubSpot File Manager.`)
    } catch (e) {
      setState('idle')
      say('warn', `${e?.message || 'The composite failed.'} Nothing was uploaded.`)
    }
  }

  return (
    <section className={`card ${styles.wrap}`}>
      <div className="field-label">Place the school&apos;s real mark on this sheet</div>
      <p className={styles.note}>
        Finds the empty plate on every housing and places the <strong>owned mark file</strong> onto each one. The mark
        stops being the model&apos;s approximation and becomes the school&apos;s actual artwork. The ring underneath is
        still drawn, so this is a concept with a real mark, not a proof.
      </p>

      <div className={styles.grid}>
        <label className={styles.gridField}>
          <span>Rows</span>
          <input className="input" type="number" min="1" max="8" value={rows}
            onChange={(e) => setRows(e.target.value)} />
        </label>
        <label className={styles.gridField}>
          <span>Per row</span>
          <input className="input" type="number" min="1" max="12" value={cols}
            onChange={(e) => setCols(e.target.value)} />
        </label>
      </div>
      {/* ⚠️ THE GRID IS STATED, NOT GUESSED. On 2026-09-19 I assumed 6/7/6 on a
          sheet that was 3 rows of 7 and scored the detector at 7 of 19 against a
          real 21. A wrong grid does not fail loudly, it quietly reports a worse
          number, so the operator sets it and the result says what it used. */}
      <p className={styles.note}>
        Count the housings on the sheet above and set the grid to match. A wrong grid does not fail, it just finds
        fewer plates, so the result below always says how many of how many.
      </p>

      <button type="button" className="btn btn-primary" disabled={state === 'working' || !sheetUrl || !markUrl} onClick={run}>
        {state === 'working' ? 'Finding the plates and placing the mark…' : 'Place the real mark'}
      </button>

      {found && (
        <p className={styles.note}>
          Plates found: <strong>{found.hits} of {found.of}</strong>.
        </p>
      )}
      {msg.text && <p className={msg.tone === 'ok' ? styles.ok : styles.warn} role="status">{msg.text}</p>}

      <canvas ref={previewRef} className={result ? styles.preview : styles.hidden} />

      {result && (
        <>
          <p className={styles.note}>
            {result.width} × {result.height}, {(result.bytes / 1048576).toFixed(2)} MB, placed in {result.seconds}s.
            Owned in HubSpot File Manager.
          </p>
          <MarkRung markSource={result.markSource} />
        </>
      )}
    </section>
  )
}
